import { BatchedMesh, Group } from 'three';
import type {
  World,
  WorldInput,
  ProgressReporter,
  WorldStats,
  Animated,
  Obstacle,
  BuildContext,
  FeatureOutput,
} from './types';
import type { Layout } from '../data/types';
import type { CollisionWorld } from '../nav/types';
import { buildSiteModel } from '../site/siteModel';
import { buildCollisionWorld } from '../nav/buildCollisionWorld';
import { createChunker } from './mesh/chunker';
import { getAllFeatures } from './features';
import { planWorldLots } from './lotPlan';
import { pavedGround } from './streetscape/pavingPieces';
import { compiledWorldPlans } from './compiledPlans';
import { planNpcLife } from './life/routinePlan';
import { createRng } from '../core/rng';
import { PLAYER, CHUNK_SIZE } from '../core/config';
import { centroid } from '../geo/polygon';
import { completeProgramAttributes } from '../render/materials/programAttributes';
import type { Vec2 } from '../geo/types';
import type { RoofProfile } from './fabric/roofProfile';

export { createProgressReporter } from './progress';

const SITE_WEIGHT = 10;
// The plan takes about a sixth of the fabric's former time (186 of 1,109 ms in node).
const LOTS_WEIGHT = 4;
// The paving takes about as long as the lot plan (189-209 ms in node).
const PAVING_WEIGHT = 4;
const FINALIZE_WEIGHT = 3;
const COLLISION_WEIGHT = 5;

export function getTotalBuildWeight(): number {
  const features = getAllFeatures();
  const featureWeight = features.reduce((sum, f) => sum + f.weight, 0);
  return (
    featureWeight + SITE_WEIGHT + LOTS_WEIGHT + PAVING_WEIGHT + FINALIZE_WEIGHT + COLLISION_WEIGHT
  );
}

export async function buildWorld(input: WorldInput, progress: ProgressReporter): Promise<World> {
  const startTime = performance.now();
  const warnings: string[] = [];
  const stageMs: Record<string, number> = {};
  const counts: Record<string, number> = {};

  const { data, seed, materials, citizens } = input;

  const site = await progress.run('Building site model', SITE_WEIGHT, () => {
    const t0 = performance.now();
    const result = buildSiteModel(data.layout);
    stageMs['site'] = performance.now() - t0;
    return result;
  });

  const root = new Group();
  const allObstacles: Obstacle[] = [];
  const allAfloat: Obstacle[] = [];
  const allAnimated: Animated[] = [];

  const chunker = createChunker(site.bounds, CHUNK_SIZE);
  const rng = createRng(seed);

  const { compiled, lots } = await progress.run('Planning lots', LOTS_WEIGHT, async () => {
    const t0 = performance.now();
    const compiled = await compiledWorldPlans(data, site, seed);
    const result = compiled?.lots ?? planWorldLots({ layout: data.layout, site }, rng);
    stageMs['lots'] = performance.now() - t0;
    return { compiled, lots: result };
  });

  const paving = await progress.run('Paving streets', PAVING_WEIGHT, () => {
    const t0 = performance.now();
    const result =
      compiled?.paving ??
      pavedGround({
        streets: data.layout.streets,
        ground: site.ground,
        houses: lots.houses,
      });
    stageMs['paving'] = performance.now() - t0;
    return result;
  });

  const lifePlan =
    compiled?.life ??
    (await progress.run('Planning daily routines', 0, () => planNpcLife(data, site, lots, seed)));

  const features = getAllFeatures();
  const crowdDoors = new Set<string>();
  const roofs: RoofProfile[] = [];

  for (const feature of features) {
    const output = await progress.run(feature.label, feature.weight, async () => {
      const t0 = performance.now();
      const featureRng = rng.fork(feature.id);
      const context: BuildContext = {
        layout: data.layout,
        palette: data.palette,
        painterView: data.painterView,
        site,
        lots,
        paving,
        terrainPlans: compiled?.terrain,
        hogeHuis: compiled?.hogeHuis,
        lifePlan,
        crowdDoors,
        roofs,
        citizens,
        rng: featureRng,
        materials,
        chunks: chunker,
        warn: (msg) => warnings.push(`[${feature.id}] ${msg}`),
      };
      let result: FeatureOutput;
      if (feature.batches) {
        const jobs = feature.batches(context);
        const objects: FeatureOutput['objects'][number][] = [];
        const obstacles: Obstacle[] = [];
        const afloat: Obstacle[] = [];
        const animated: Animated[] = [];
        const batchCounts: Record<string, number> = {};
        for (const job of jobs) {
          const batch = await progress.run(job.label, 0, job.build);
          objects.push(...batch.objects);
          obstacles.push(...batch.obstacles);
          afloat.push(...(batch.afloat ?? []));
          animated.push(...batch.animated);
          for (const [key, count] of Object.entries(batch.counts))
            batchCounts[key] = (batchCounts[key] ?? 0) + count;
        }
        result = { objects, obstacles, afloat, animated, counts: batchCounts };
      } else result = feature.build(context);
      stageMs[feature.id] = performance.now() - t0;
      return result;
    });

    for (const obj of output.objects) {
      root.add(obj);
    }
    allObstacles.push(...output.obstacles);
    allAfloat.push(...(output.afloat ?? []));
    allAnimated.push(...output.animated);

    for (const [key, value] of Object.entries(output.counts)) {
      counts[key] = (counts[key] ?? 0) + value;
    }
  }

  const chunkMeshes = await progress.run('Finalizing chunks', FINALIZE_WEIGHT, () => {
    const t0 = performance.now();
    const meshes = chunker.finalize(materials.painted());
    stageMs['finalize'] = performance.now() - t0;
    return meshes;
  });

  for (const mesh of chunkMeshes) {
    root.add(mesh);
  }
  completeProgramAttributes(root);

  const collision = await progress.run('Building collision', COLLISION_WEIGHT, () => {
    const t0 = performance.now();
    const result = buildCollisionWorld(site, allObstacles, PLAYER.radius);
    stageMs['collision'] = performance.now() - t0;
    return result;
  });

  const spawn = findSpawn(data.layout, collision);

  const buildMs = performance.now() - startTime;

  let triangles = 0;
  let geometryBytes = 0;
  let meshCount = 0;

  root.traverse((obj) => {
    if ('geometry' in obj && obj.geometry) {
      meshCount++;
      const geom = obj.geometry as {
        index?: { count: number };
        attributes?: Record<string, { array: { byteLength: number } }>;
      };
      if (geom.index) {
        triangles += geom.index.count / 3;
      }
      if (geom.attributes) {
        for (const attr of Object.values(geom.attributes)) {
          geometryBytes += attr.array.byteLength;
        }
      }
    }
  });

  const stats: WorldStats = {
    seed,
    buildMs,
    stageMs,
    counts,
    meshes: meshCount,
    triangles,
    geometryBytes,
    warnings,
  };

  return {
    root,
    lifePlan,
    site,
    collision,
    obstacles: allObstacles,
    afloat: allAfloat,
    animated: allAnimated,
    spawn,
    stats,
    dispose(): void {
      root.traverse((obj) => {
        if (obj instanceof BatchedMesh) obj.dispose();
        else if ('geometry' in obj && obj.geometry) {
          (obj.geometry as { dispose(): void }).dispose();
        }
      });
    },
  };
}

const SPAWN_SQUARE_NAME = /^dam$/i;
const SPAWN_FALLBACK: Vec2 = [0, 0];

/* The fallback also goes through nearestWalkable, so a layout without a Dam never spawns the
   player inside a building or on water. */
export function findSpawn(layout: Layout, collision: CollisionWorld): Vec2 {
  const dam = layout.squares.find((square) => SPAWN_SQUARE_NAME.test(square.name));
  return collision.nearestWalkable(dam ? centroid(dam.polygon) : SPAWN_FALLBACK);
}
